<template>
  <main>
    <div
      class="app-sidebar bg-white py-4 px-4"
      :class="{ opened: isOpenSidebar }"
    >
      <router-link :to="logoRoute">
        <AppLogo :size="40" />
      </router-link>
      <div class="app-sidebar-menu overflow-y-auto block">
        <MenuList class="mt-2" />
      </div>
      <SidebarFooterActions />
    </div>
    <div class="app-content" :class="{ opened: isOpenSidebar }">
      <div class="flex justify-between px-8 py-1 bg-surface-100">
        <div class="flex items-center">
          <i
            class="pi pi-bars cursor-pointer"
            @click="isOpenSidebar = !isOpenSidebar"
          />

          <TFABreadcrumb
            v-if="props.breadcrumb.length"
            :breadcrumb="props.breadcrumb"
          />
          <span v-else-if="props?.title" class="font-semibold px-4 py-3">
            {{ props.title }}
          </span>

          <!-- Organization Context Indicators -->
          <div class="ml-4 flex items-center gap-2">
            <!-- Home Organization Pill -->
            <span
              class="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1"
            >
              <i class="pi pi-home text-xs" />
              Home: {{ authStore.defaultOrg?.name }}
            </span>

            <!-- Impersonation Indicator -->
            <span
              v-if="impersonationStore.isImpersonating"
              class="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1"
            >
              <i class="pi pi-eye text-xs" />
              Viewing: {{ impersonationStore.currentOrganization?.name }}
              <button
                class="text-orange-600 hover:text-orange-900 ml-1"
                title="Exit impersonation"
                @click="exitImpersonation"
              >
                <i class="pi pi-times text-xs" />
              </button>
            </span>

            <!-- Organization Type Indicator (Network Admins & TFA Employees Only) - Moved to end -->
            <span
              v-if="
                currentOrganizationType === 'network' &&
                shouldShowOrganizationType
              "
              class="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1"
            >
              <i class="pi pi-sitemap text-xs" />
              Network Organization
            </span>
            <span
              v-else-if="
                currentOrganizationType === 'club' && shouldShowOrganizationType
              "
              class="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1"
            >
              <i class="pi pi-building text-xs" />
              Club Organization
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <AdminNotificationCenter />
          <AdminAvatar class="cursor-pointer" @click="toggle" />
        </div>
        <Menu id="profile-menu" ref="menu" :model="items" :popup="true" />
      </div>

      <div class="p-4 relative">
        <slot />
      </div>
      <div class="p-4 relative text-center">
        <div class="text-xs">
          Copyright &copy; {{ currentYear }} TFA, All rights reserved.
          <a
            class="text-blue-600 underline hover:text-blue-800"
            href="https://www.thefuturesapp.com/terms-of-service"
            target="_blank"
            >Terms of Service</a
          >
        </div>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import AppLogo from "@/shared/components/AppLogo.vue";
import AdminAvatar from "@/shared/components/AdminAvatar.vue";
import AdminNotificationCenter from "@/shared/components/AdminNotificationCenter.vue";
import MenuList from "@/shared/components/MenuList.vue";
import SidebarFooterActions from "@/shared/components/SidebarFooterActions.vue";
import Menu from "primevue/menu";
import { ROUTE_NAME } from "@/shared/constants/routes";
import { getDefaultLandingRouteName } from "@/shared/utils/defaultLandingRoute";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { useImpersonationStore } from "@/modules/auth/stores/impersonation";
import { watch, ref, computed } from "vue";
import { useWindowSize } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import { useRouterNavigation } from "@/composables/useRouterNavigation";
const authStore = useAuthStore();
const { navigateWithQuery } = useRouterNavigation();
const impersonationStore = useImpersonationStore();
const route = useRoute();
const router = useRouter();
import type { MenuItem } from "primevue/menuitem";
import TFABreadcrumb from "@/shared/components/TFABreadcrumb.vue";

interface Props {
  title?: string;
  breadcrumb?: MenuItem[];
}
const props = withDefaults(defineProps<Props>(), {
  title: "Page",
  breadcrumb: () => [],
});

const { width } = useWindowSize();

const isLargeScreen = computed(() => width.value >= 992);
const isOpenSidebar = ref(true);

// Get current organization type
const currentOrganizationType = computed(() => {
  const orgType = impersonationStore.currentOrganization?.organization_type;
  return orgType;
});

// Check if user should see organization type indicators
const shouldShowOrganizationType = computed(() => {
  const isTfaEmployee = authStore.isTfaEmployee;
  const isNetworkAdmin = authStore.defaultOrg?.organization_type === "network";
  return isTfaEmployee || isNetworkAdmin;
});

// Logo home uses the same org-type landing as login (MAI-4748): clubs →
// Schedule, networks → Network Management, else Feed.
const logoRoute = computed(() => {
  const orgType =
    impersonationStore.currentOrganization?.organization_type ??
    authStore.defaultOrg?.organization_type;

  return {
    name: getDefaultLandingRouteName(orgType),
    query: route.query,
  };
});

watch(isLargeScreen, (isLarge) => {
  isOpenSidebar.value = isLarge;
});

watch(isLargeScreen, (isLarge) => {
  isOpenSidebar.value = isLarge;
});

const menu = ref();
const toggle = (event: any) => {
  menu.value.toggle(event);
};

// Exit impersonation function
const exitImpersonation = () => {
  impersonationStore.exitImpersonation();

  // Remove impersonation parameter from URL and navigate to Organizations page
  const newQuery = { ...route.query };
  delete newQuery.impersonateOrg;

  // Use router.push directly to avoid navigateWithQuery preserving the old query
  router.push({
    name: ROUTE_NAME.NETWORK_MANAGEMENT,
    query: newQuery,
  });
};
const items = ref([
  {
    label: "Profile",
    icon: "pi pi-user",
    command: async () => {
      await navigateWithQuery({
        name: ROUTE_NAME.PROFILE_UPDATE,
        query: route.query, // Preserve query parameters
      });
    },
  },
  {
    label: "Logout",
    icon: "pi pi-sign-out",
    command: () => {
      authStore.logout();
    },
  },
]);

const currentYear = new Date().getFullYear();
</script>

<style scoped>
.app-sidebar {
  position: fixed;
  display: flex;
  flex-direction: column;
  width: 240px;
  height: 100vh;
  transform: translateX(-240px);
  transition: transform 0.3s ease-in-out;
}

.app-sidebar.opened {
  transform: translateX(0);
}

/* Let the nav take the leftover height and scroll inside itself, so the pinned
   support row below it stays on screen no matter how long the menu gets. */
.app-sidebar-menu {
  flex: 1 1 auto;
  min-height: 0;
}

.app-content {
  width: 100%;
  transition:
    width 0.3s ease-in-out,
    margin-left 0.3s ease-in-out;
}

.app-content.opened {
  margin-left: 240px;
  width: calc(100% - 240px);
}
</style>
